import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DELETABLE_MAP,
  cascadeTotal,
  type DeletableEntity,
  type DeletionCheck,
} from '@goyone/shared';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { Modal } from './ui';
import { useToast } from './Toast';
import { Icon } from './Icon';

/**
 * نافذة حذف واحدة لكل الأنواع.
 *
 * تسأل الخادم أولًا عمّا سيقع، ثم تعرضه: ما يمنع الحذف، أو ما سيُحذف مع
 * السجل بأسمائه وأعداده. نافذة تقول «هل أنت متأكد؟» وحدها لا تُعطي من
 * يقرأها ما يبني عليه قرارًا.
 */
export function DeleteDialog({
  entity,
  id,
  endpoint,
  onClose,
  onDeleted,
}: {
  entity: DeletableEntity;
  id: string;
  /** مسار الحذف — يختلف عن مسار الفحص لأن لكل نوع متحكّمه. */
  endpoint: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const def = DELETABLE_MAP[entity];
  const [typed, setTyped] = useState('');
  const toast = useToast();

  const check = useQuery({
    queryKey: ['deletion', entity, id],
    queryFn: () => api<DeletionCheck>(`/deletion/${entity}/${id}`),
  });

  const remove = useMutation({
    mutationFn: () => api(endpoint, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      /*
       * التأكيد يذكر ما حُذف بالاسم.
       *
       * الحذف لا يُسترجع، ورسالةٌ عامة لا تُطمئن من يخشى أنه ضغط على الصفّ
       * الخطأ. والاسم يُقرأ من نتيجة الفحص لا من السجل — فالسجل ذهب.
       */
      toast(`حُذف ${def.singular}${check.data?.name ? ` «${check.data.name}»` : ""}`);
      onDeleted();
    },
  });

  const data = check.data;
  const total = data ? cascadeTotal(data) : 0;

  /*
   * كتابة الاسم شرطٌ حين يُجرّ معه غيره.
   *
   * الضغط على «حذف» يصير تلقائيًا بعد المرة العاشرة. وكتابة الاسم تُجبر
   * على قراءة ما يُحذف — وهي الفرق بين حذفٍ مقصود وحذفٍ بالعادة.
   */
  const needsTyping = total > 0;
  const confirmed = !needsTyping || typed.trim() === data?.name.trim();

  return (
    <Modal
      title={`حذف ${def.singular}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          {data?.canDelete && (
            <button
              className="btn btn-danger"
              onClick={() => remove.mutate()}
              disabled={!confirmed || remove.isPending}
            >
              {remove.isPending ? 'جارٍ الحذف' : 'حذف نهائي'}
            </button>
          )}
        </>
      }
    >
      {check.isLoading && <div className="skeleton" style={{ height: 90 }} />}

      {check.isError && (
        <div className="alert alert-danger">
          {check.error instanceof Error ? check.error.message : 'تعذّر الفحص.'}
        </div>
      )}

      {remove.isError && (
        <div className="alert alert-danger mb-4">
          {remove.error instanceof Error ? remove.error.message : 'تعذّر الحذف.'}
        </div>
      )}

      {data && !data.canDelete && (
        <>
          <div className="alert alert-danger mb-4">
            <Icon name="alert" size={18} />
            <div>
              لا يُحذف {def.singular} «{data.name}» لارتباطه بسجلات مالية أو محاسبية.
            </div>
          </div>

          <dl className="detail-list">
            {data.blockers.map((b) => (
              <div key={b.label} style={{ display: 'contents' }}>
                <dt>{b.label}</dt>
                <dd className="num">{b.count}</dd>
              </div>
            ))}
          </dl>

          {data.alternative && (
            <div className="alert alert-info mt-4">
              <Icon name="zap" size={18} />
              <div>{data.alternative}</div>
            </div>
          )}
        </>
      )}

      {data?.canDelete && (
        <>
          <p className="mb-4">
            سيُحذف {def.singular} «<strong>{data.name}</strong>» نهائيًا، ولا يُسترجع.
          </p>

          {total > 0 && (
            <>
              <div className="alert alert-warning mb-4">
                <Icon name="alert" size={18} />
                <div>ويُحذف معه ما يلي:</div>
              </div>

              <dl className="detail-list">
                {data.cascades.map((c) => (
                  <div key={c.label} style={{ display: 'contents' }}>
                    <dt>{c.label}</dt>
                    <dd className="num">{c.count}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}

          {/*
            ما يفقد ارتباطه معروض ولو لم يُحذف شيء.
            من يحذف صفقةً له أن يعرف أن عروض أسعارها ستبقى بلا نسبة إليها،
            فذلك ما يجعله يختار الإغلاق بخسارة بدل الحذف.
          */}
          {data.unlinks.length > 0 && (
            <>
              <div className={`alert alert-info ${total > 0 ? 'mt-4' : ''} mb-4`}>
                <Icon name="zap" size={18} />
                <div>ويبقى ما يلي ويفقد ارتباطه به:</div>
              </div>

              <dl className="detail-list">
                {data.unlinks.map((u) => (
                  <div key={u.label} style={{ display: 'contents' }}>
                    <dt>{u.label}</dt>
                    <dd className="num">{u.count}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}

          {total === 0 && data.unlinks.length === 0 && (
            <div className="alert alert-info">لا شيء مرتبط به، فلا يُحذف معه غيره.</div>
          )}

          {/*
            كتابة الاسم مقصورة على الحذف المتتالي.
            فرضُها على ما يفقد ارتباطه وحده يجعلها طقسًا يُؤدَّى في كل مرة،
            وما يُفعل في كل مرة لا يُقرأ.
          */}
          {needsTyping && (
            <div className="field mt-4">
              <label className="label req" htmlFor="del-confirm">
                اكتب «{data.name}» للتأكيد
              </label>
              <input
                id="del-confirm"
                className="input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
              />
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

/**
 * زر الحذف.
 *
 * يظهر لمن يملك الصلاحية وحده. عرضه للجميع ثم ردّ الخادم بالمنع يجعل
 * المستخدم يظن العطل في النظام لا في صلاحيته.
 */
export function DeleteButton({
  entity,
  id,
  endpoint,
  onDeleted,
  label = 'حذف',
  ariaLabel,
  className = 'btn btn-danger',
}: {
  entity: DeletableEntity;
  id: string;
  endpoint: string;
  onDeleted: () => void;
  label?: string;
  /**
   * بديل النصّ حين يكون الزر أيقونة وحدها في صفّ جدول.
   *
   * صفوف الجدول تحمل أزرارًا متكرّرة، فزرّ بلا نصّ يُقرأ على قارئ الشاشة
   * «زر» بلا ما يميّزه عن مثيله في الصفّ التالي. وذكر اسم السجل فيه يجعل
   * التنقّل بين الصفوف مفهومًا بلا نظر إلى الشاشة.
   */
  ariaLabel?: string;
  className?: string;
}) {
  const { atLeast } = useAuth();
  const [open, setOpen] = useState(false);
  const def = DELETABLE_MAP[entity];

  if (!atLeast(def.minRole)) return null;

  return (
    <>
      {/* type="button" صراحةً: الزر يُستعمل داخل نماذج، والافتراضي إرسالها. */}
      <button
        className={className}
        type="button"
        aria-label={ariaLabel}
        onClick={() => setOpen(true)}
      >
        <Icon name="trash" size={15} />
        {label}
      </button>

      {open && (
        <DeleteDialog
          entity={entity}
          id={id}
          endpoint={endpoint}
          onClose={() => setOpen(false)}
          onDeleted={() => {
            setOpen(false);
            onDeleted();
          }}
        />
      )}
    </>
  );
}
